import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
    BUILDING_NAMES,
    RESOURCE_NAMES,
    TEAM_PLANET_BUILDING_QUEUE_LIMIT,
    TEAM_PLANET_ECONOMY_MULTIPLIER,
    TEAM_PLANET_SCIENCE_QUEUE_LIMIT,
    TEAM_PLANET_SHIP_BUILD_LANES,
    type Planet,
    type PlayerRace,
    type TickCheckpointEventOf,
} from '@kaeliux/shared';
import { calculateTeamPlanetUsedPopulation } from '@kaeliux/game-engine';
import { useAuthStore } from '../store/authStore';
import { useShellChrome } from '../hooks/useShellChrome';
import { useSurfaceFits } from '../hooks/useSurfaceFits';
import { usePlanetView } from '../contexts/PlanetViewContext';
import { useTeamPlanetSession } from '../contexts/TeamPlanetContext';
import PlanetSurface from '../components/planet/PlanetSurface';
import { PlanetZoneLinksFallback } from '../components/planet/PlanetZoneLinksFallback';
import TeamPlanetReadOnlyNotice from '../components/teamPlanet/TeamPlanetReadOnlyNotice';
import { Panel } from '../components/ui/Panel';
import { Button } from '../components/ui/Button';
import { Icon } from '../components/ui/Icon';
import { ProgressBar } from '../components/ui/ProgressBar';
import { linkButtonClasses } from '../components/ui/buttonStyles';
import { zoneAccentStyle } from '../components/planet/zoneAccent';
import { countPendingBuildsByZone, pendingBuilds } from '../utils/buildQueue';
import { getBuildingIcon } from '../utils/buildingIcons';
import { formatFullNumber } from '../utils/formatters';
import { RACE_LABELS } from '../utils/teamPlanetStyle';
import { cn } from '../utils/cn';

/** Same page-chrome reserve `PlanetPage` gives its surface stage. */
const PAGE_CHROME_RESERVE_PX = 96;

function planetImage(planetType: number): string {
    return new URL(`../assets/images/planets/planet_${planetType}.png`, import.meta.url).href;
}

function coordsOf(planet: Planet): string {
    return `${planet.coordinates.galaxy}:${planet.coordinates.system}:${planet.coordinates.position}`;
}

function pendingOf<T extends 'shipBuild' | 'scienceResearch' | 'shipUpgrade'>(planet: Planet, type: T): TickCheckpointEventOf<T>[] {
    return (planet.tickCheckpoints ?? []).filter(
        (e): e is TickCheckpointEventOf<T> => e.eventType === type && !e.cancelled,
    );
}

function Perk({ label, value }: { label: string; value: ReactNode }) {
    return (
        <div className="flex flex-col gap-0.5">
            <dt className="text-micro text-content-muted">{label}</dt>
            <dd className="text-body font-semibold text-content-primary tabular-nums">{value}</dd>
        </div>
    );
}

interface FacilityProps {
    to: string;
    testId: string;
    title: string;
    level: number;
    notBuiltHint: string;
    sprites: { key: string; src: string; caption?: string }[];
    status: ReactNode;
}

/**
 * One of the Team planet's two facilities, kept off the surface so research
 * and the shared shipyards read as their own destinations. The whole card is
 * the link; an unbuilt facility sends the player to where it is constructed.
 */
function Facility({ to, testId, title, level, notBuiltHint, sprites, status }: FacilityProps) {
    const built = level > 0;
    return (
        <Link
            to={to}
            data-testid={testId}
            className="group block rounded-panel focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--zone-accent)]"
            style={zoneAccentStyle('military')}
        >
            <Panel
                variant="solid"
                padding="md"
                className="flex h-full flex-col gap-3 transition-colors group-hover:border-[color:var(--zone-accent)]"
            >
                <div className="flex items-end gap-2">
                    {sprites.map((sprite) => (
                        <figure key={sprite.key} className="flex flex-col items-center gap-1">
                            <img
                                src={sprite.src}
                                alt=""
                                className={cn(
                                    'h-20 w-16 rounded-tile border border-hairline bg-space-950 object-cover transition-transform duration-300 group-hover:-translate-y-0.5',
                                    !built && 'opacity-40 grayscale',
                                )}
                            />
                            {sprite.caption && <figcaption className="text-micro text-content-muted">{sprite.caption}</figcaption>}
                        </figure>
                    ))}
                </div>
                <div className="flex items-baseline justify-between gap-2">
                    <h3 className="text-title text-content-primary">{title}</h3>
                    <span className="text-label text-content-muted tabular-nums">{built ? `Level ${level}` : 'Not built'}</span>
                </div>
                <div className="text-body text-content-secondary">{built ? status : notBuiltHint}</div>
            </Panel>
        </Link>
    );
}

export default function TeamPlanetPage() {
    const { teamPlanet, alliance, isLeader } = useTeamPlanetSession();
    const view = usePlanetView();
    const { profile } = useAuthStore();
    const { hudHeight, navHeight } = useShellChrome();
    const surfaceFits = useSurfaceFits(hudHeight + navHeight + PAGE_CHROME_RESERVE_PX);
    const stageHeight = `calc(100vh - ${hudHeight + navHeight + PAGE_CHROME_RESERVE_PX}px)`;

    const zoneQueueCounts = useMemo(() => countPendingBuildsByZone(teamPlanet), [teamPlanet]);
    const shipQueue = useMemo(() => pendingOf(teamPlanet, 'shipBuild'), [teamPlanet]);
    const research = useMemo(() => pendingOf(teamPlanet, 'scienceResearch'), [teamPlanet]);
    const buildQueueCount = useMemo(() => pendingBuilds(teamPlanet).length, [teamPlanet]);

    const usedPopulation = calculateTeamPlanetUsedPopulation(teamPlanet.teamShips, teamPlanet.tickCheckpoints ?? []);
    const populationCapacity = teamPlanet.resources.population.capacity;

    const laboratoryLevel = teamPlanet.buildings.laboratory?.level ?? 0;
    const shipyardLevel = teamPlanet.buildings.shipyard?.level ?? 0;
    const laneCount = (race: PlayerRace) => shipQueue.filter((e) => e.eventDetails.queueLane === race).length;
    const upgradeLaneCount = pendingOf(teamPlanet, 'shipUpgrade').filter((e) => e.eventDetails.queueLane === profile?.race).length;

    return (
        <main className="flex-1 w-full max-w-7xl mx-auto px-4 py-4 flex flex-col gap-4" data-testid="team-planet-page">
            {!view.canManage && <TeamPlanetReadOnlyNotice />}

            {surfaceFits ? (
                <div
                    className="relative w-full overflow-hidden rounded-panel border border-space-700"
                    style={{ height: stageHeight }}
                    data-testid="team-planet-surface"
                >
                    <PlanetSurface planet={teamPlanet} race={profile?.race} />
                </div>
            ) : (
                <PlanetZoneLinksFallback
                    zoneQueueCounts={zoneQueueCounts}
                    shipQueueCount={shipQueue.length}
                    buildingLinks={[
                        { to: '/team-planet/sciences', icon: 'science', label: BUILDING_NAMES.laboratory, count: research.length },
                        { to: '/team-planet/shipyard', icon: 'shipyard', label: BUILDING_NAMES.shipyard, count: shipQueue.length },
                        { to: '/team-planet/starport', icon: 'starport', label: BUILDING_NAMES.techCenter, count: upgradeLaneCount },
                    ]}
                />
            )}

            <div className="grid gap-4 lg:grid-cols-12">
                <Panel variant="solid" padding="md" className="flex flex-col gap-4 lg:col-span-5">
                    <div className="flex items-center gap-4">
                        <img
                            src={planetImage(teamPlanet.planetType)}
                            alt=""
                            className="h-20 w-20 shrink-0 rounded-full border border-hairline-strong object-cover"
                        />
                        <div className="min-w-0">
                            <p className="text-label text-content-muted">Team planet of [{alliance.tag}] {alliance.name}</p>
                            <h2 className="text-display text-content-primary truncate">{teamPlanet.name}</h2>
                            <p className="text-body text-content-muted font-mono" data-testid="team-planet-coordinates">
                                {coordsOf(teamPlanet)}
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <div className="flex justify-between text-body text-content-secondary">
                            <span>Population</span>
                            <span className="tabular-nums">{formatFullNumber(usedPopulation)} / {formatFullNumber(populationCapacity)}</span>
                        </div>
                        <ProgressBar value={populationCapacity > 0 ? usedPopulation / populationCapacity : 0} />
                    </div>

                    <dl className="grid grid-cols-2 gap-x-4 gap-y-3 border-t border-hairline pt-3">
                        <Perk label="Income and storage" value={`${TEAM_PLANET_ECONOMY_MULTIPLIER}× normal`} />
                        <Perk label={RESOURCE_NAMES.ozone} value="Never depletes" />
                        <Perk label="Build queue" value={`${buildQueueCount} / ${TEAM_PLANET_BUILDING_QUEUE_LIMIT}`} />
                        <Perk label="Research queue" value={`${research.length} / ${TEAM_PLANET_SCIENCE_QUEUE_LIMIT}`} />
                    </dl>

                    <div className="flex flex-col gap-1 border-t border-hairline pt-3">
                        {isLeader ? (
                            <Link
                                to="/team-planet/fleets"
                                className={cn(linkButtonClasses('primary', 'md'), 'self-start')}
                                data-testid="team-planet-send-fleet-link"
                            >
                                <Icon name="fleets" size={16} aria-hidden="true" />
                                Send Fleet
                            </Link>
                        ) : (
                            <>
                                <Button type="button" variant="secondary" disabled className="self-start" data-testid="team-planet-send-fleet-disabled">
                                    Send Fleet
                                </Button>
                                <span className="text-micro text-content-muted">
                                    Only the alliance leader can dispatch fleets from the Team planet
                                </span>
                                <Link
                                    to="/team-planet/fleets"
                                    className={cn(linkButtonClasses('secondary', 'sm'), 'self-start')}
                                    data-testid="team-planet-view-flights-link"
                                >
                                    View Flights
                                </Link>
                            </>
                        )}
                    </div>
                </Panel>

                <div className="grid gap-4 sm:grid-cols-2 lg:col-span-7 content-start">
                    <Facility
                        to="/team-planet/sciences"
                        testId="team-planet-facility-sciences"
                        title={BUILDING_NAMES.laboratory}
                        level={laboratoryLevel}
                        notBuiltHint={`Construct the ${BUILDING_NAMES.laboratory} to start alliance research.`}
                        sprites={[{
                            key: 'laboratory',
                            src: getBuildingIcon('laboratory', laboratoryLevel, view.buildingRace('laboratory') ?? profile?.race),
                        }]}
                        status={research.length > 0
                            ? `${research.length} of ${TEAM_PLANET_SCIENCE_QUEUE_LIMIT} research slots in use`
                            : 'No research in progress'}
                    />
                    <Facility
                        to="/team-planet/shipyard"
                        testId="team-planet-facility-shipyard"
                        title={BUILDING_NAMES.shipyard}
                        level={shipyardLevel}
                        notBuiltHint={`Construct the ${BUILDING_NAMES.shipyard} to open all three race lanes.`}
                        sprites={TEAM_PLANET_SHIP_BUILD_LANES.map((race) => ({
                            key: race,
                            src: getBuildingIcon('shipyard', shipyardLevel, race),
                            caption: RACE_LABELS[race],
                        }))}
                        status={(
                            <span className="flex flex-wrap gap-x-3">
                                {TEAM_PLANET_SHIP_BUILD_LANES.map((race) => (
                                    <span key={race} className="tabular-nums">
                                        {RACE_LABELS[race]}: {laneCount(race)} queued
                                    </span>
                                ))}
                            </span>
                        )}
                    />
                </div>
            </div>
        </main>
    );
}
